import { motion } from "framer-motion";
import { 
  Building2, 
  Smartphone, 
  Zap, 
  Heart, 
  Landmark, 
  Wallet, 
  Car,
  GraduationCap,
  Shield,
  ArrowRight,
  Check,
  RefreshCw,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const OFFICIAL_SOURCES = [
  { 
    name: "Impôts", 
    icon: Landmark, 
    gradient: "from-blue-500 to-indigo-600",
    glow: "shadow-blue-500/30",
    examples: ["Avis d'imposition", "Déclarations fiscales"]
  },
  { 
    name: "Santé", 
    icon: Heart, 
    gradient: "from-rose-500 to-pink-600",
    glow: "shadow-rose-500/30",
    examples: ["Ameli", "Mutuelles", "Remboursements"]
  },
  { 
    name: "Télécom", 
    icon: Smartphone, 
    gradient: "from-orange-500 to-red-500",
    glow: "shadow-orange-500/30",
    examples: ["Orange", "Free", "SFR", "Bouygues"]
  },
  { 
    name: "Énergie", 
    icon: Zap, 
    gradient: "from-amber-400 to-orange-500",
    glow: "shadow-amber-500/30",
    examples: ["EDF", "Engie", "Total Energies"]
  },
  { 
    name: "Banques", 
    icon: Wallet, 
    gradient: "from-emerald-500 to-teal-600",
    glow: "shadow-emerald-500/30",
    examples: ["Relevés bancaires", "RIB"]
  },
  { 
    name: "Retraite", 
    icon: GraduationCap, 
    gradient: "from-violet-500 to-purple-600",
    glow: "shadow-violet-500/30",
    examples: ["AGIRC-ARRCO", "CNAV"]
  },
  { 
    name: "Auto", 
    icon: Car, 
    gradient: "from-slate-500 to-zinc-600",
    glow: "shadow-slate-500/30",
    examples: ["Assurance auto", "Carte grise"]
  },
  { 
    name: "URSSAF", 
    icon: Building2, 
    gradient: "from-indigo-500 to-blue-600",
    glow: "shadow-indigo-500/30",
    examples: ["Attestations", "Cotisations"]
  },
];

// Floating document component for animation
const FloatingDoc = ({ delay, x, type }: { delay: number; x: number; type: string }) => (
  <motion.div
    initial={{ y: -20, opacity: 0 }}
    animate={{ 
      y: [0, 120, 120],
      opacity: [0, 1, 0]
    }}
    transition={{
      duration: 3,
      delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
    className="absolute"
    style={{ left: `${x}%` }}
  >
    <div className="w-8 h-10 rounded-md bg-gradient-to-br from-white to-gray-100 dark:from-white/20 dark:to-white/5 border border-white/50 dark:border-white/20 shadow-lg flex items-center justify-center">
      <FileText className="w-4 h-4 text-primary/60" />
    </div>
    <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground whitespace-nowrap font-medium">
      {type}
    </span>
  </motion.div>
);

export const EasySyncSection = () => {
  return (
    <section className="py-20 md:py-32 px-6 relative overflow-hidden">
      {/* Premium background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/3 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-[120px]" />
      </div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          {/* Badge ACPR */}
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-medium mb-6"
            whileHover={{ scale: 1.02 }}
          >
            <Shield className="w-4 h-4" />
            <span>Agrément ACPR · Connexion sécurisée bancaire</span>
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-5">
            Synchronisation automatique avec{" "}
            <span className="text-gradient">50+ organismes officiels</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Connectez vos comptes une seule fois. Vos factures, attestations et documents officiels arrivent automatiquement, sans aucune action de votre part.
          </p>
        </motion.div>

        {/* Main content: Grid + Animation */}
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Sources Grid - 3 columns */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3"
          >
            {OFFICIAL_SOURCES.map((source, index) => {
              const Icon = source.icon;
              return (
                <motion.div
                  key={source.name}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
                  whileHover={{ y: -4, scale: 1.02 }}
                  className="group relative"
                >
                  {/* Card */}
                  <div className="relative p-4 rounded-2xl backdrop-blur-xl bg-white/70 dark:bg-white/5 border border-white/40 dark:border-white/10 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                    {/* Glow on hover */}
                    <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${source.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                    
                    {/* Icon */}
                    <div className={`relative w-11 h-11 rounded-xl bg-gradient-to-br ${source.gradient} flex items-center justify-center mb-3 shadow-lg ${source.glow} group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="font-semibold text-sm mb-1.5 relative">{source.name}</h3>
                    <p className="text-[11px] text-muted-foreground leading-snug relative line-clamp-2">
                      {source.examples.join(" · ")}
                    </p>
                    
                    {/* Sync indicator */}
                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Sync Animation Visual - 2 columns */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-2"
          >
            <div className="relative p-6 rounded-3xl backdrop-blur-xl bg-gradient-to-br from-white/80 to-white/40 dark:from-white/10 dark:to-white/5 border border-white/50 dark:border-white/10 shadow-2xl shadow-black/5">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold text-sm">EasySync</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Actif
                </div>
              </div>

              {/* Animated flow */}
              <div className="relative h-40 mb-6 overflow-hidden rounded-xl bg-gradient-to-b from-muted/30 to-muted/10">
                <FloatingDoc delay={0} x={15} type="Facture" />
                <FloatingDoc delay={0.8} x={40} type="Avis" />
                <FloatingDoc delay={1.6} x={65} type="Relevé" />
                <FloatingDoc delay={2.4} x={85} type="Attestation" />
                
                {/* Bottom sync bar */}
                <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-primary/20 to-transparent flex items-end justify-center pb-2">
                  <motion.div
                    animate={{ width: ["0%", "100%", "0%"] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    className="h-1 bg-gradient-to-r from-transparent via-primary to-transparent rounded-full"
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-muted/30 text-center">
                  <div className="text-2xl font-bold text-gradient">50+</div>
                  <div className="text-xs text-muted-foreground">Organismes</div>
                </div>
                <div className="p-3 rounded-xl bg-muted/30 text-center">
                  <div className="text-2xl font-bold text-gradient">24/7</div>
                  <div className="text-xs text-muted-foreground">Synchronisation</div>
                </div>
              </div>

              {/* Benefits list */}
              <div className="mt-5 space-y-2">
                {[
                  "Import automatique des documents",
                  "Classification IA instantanée",
                  "Alertes avant échéances"
                ].map((benefit, i) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-2.5 h-2.5 text-primary" />
                    </div>
                    {benefit}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link to="/auth?mode=signup">
            <Button variant="glow" size="lg" className="group h-12 px-8">
              Activer EasySync gratuitement
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-500" />
            Données chiffrées · Sans accès à vos identifiants
          </p>
        </motion.div>
      </div>
    </section>
  );
};
